<template>
	<GlassCard class="card p-3">
		<!-- Basic display logic: if there's an image, show it; otherwise show some text -->
		<image-loader
			v-if="itemData.imageSrc"
			:src="itemImage"
			alt="Processed Image"
			class="image" />
		<!-- <h1 v-else>{{ itemData.dp ?? "No Image" }}</h1> -->
		<h2 v-else v-text="itemData.prompt"></h2>

		<div class="status">
			<!-- If offline, show offline message -->
			<div v-if="!isOnline" class="network-offline">Network is offline.</div>

			<!-- Else show statuses -->
			<template v-else>
				<!-- sending -->
				<div v-if="itemData.status === 'sending'">
					<div class="loading-spinner">
						<div class="status-update">Sending to Server...</div>
					</div>
					<button @click="removeProcess" class="btn btn-sm btn-warning mx-2">
						Cancel
					</button>
				</div>

				<!-- processing -->
				<div v-else-if="itemData.status === 'processing'">
					<div class="loading-spinner">
						<div class="status-update">{{ statusUpdate }}</div>
					</div>
					<button
						@click="removeProcess"
						class="btn btn-sm btn-warning mx-2 mt-3">
						Cancel
					</button>
				</div>

				<!-- error -->
				<div v-else-if="itemData.status === 'error'" class="error">
					<p>{{ errorMessage }}</p>
					<button
						@click="retryProcess"
						class="btn btn-lg btn-primary mx-2 text-white">
						Retry
					</button>
					<button @click="removeProcess" class="btn btn-lg btn-danger mx-2">
						Remove
					</button>
				</div>

				<!-- completed -->
				<div v-else-if="itemData.status === 'completed'">
					<p>Item completed!</p>
				</div>
			</template>
		</div>
	</GlassCard>
</template>

<script setup>
	import GlassCard from "./GlassCard.vue"
	import {
		ref,
		reactive,
		onMounted,
		onBeforeUnmount,
		computed,
		watch,
	} from "vue"
	import { useItemQueueStore } from "@/stores/item-queue"
	import Swal from "sweetalert2"
	import ImageLoader from "./ImageLoader.vue"
	import moment from "moment-timezone"
	import { isOnline } from "@/utils/networkBus"
	import { fetchWithAuth } from "@/utils/api-client"
	import getURL from "@/utils/url"
	import { lastMealAdded } from "@/utils/mealBus"
	import { useUserStore } from "@/stores/user"

	const userStore = useUserStore()

	const props = defineProps({
		item: {
			type: Object,
			required: true,
		},
	})

	// Reactive local copy of props
	const itemData = reactive({ ...props.item })

	// Store references
	const iqStore = useItemQueueStore()

	// Computed image URL
	const itemImage = computed(() => {
		if (!itemData.imageSrc) return null
		if (
			itemData.imageSrc.startsWith("data:") ||
			itemData.imageSrc.startsWith("blob")
		) {
			return itemData.imageSrc
		} else {
			return getURL("image", itemData.imageSrc)
		}
	})

	// Status labels
	const statusUpdate = ref("Sending...")
	const errorMessage = ref(null)

	// Single concurrency flags
	let isSending = false
	let isPolling = false
	let pollTimer = null

	// For scheduling retries of initial sending if it fails
	let retryTimer = null

	// Poll every 5s if needed
	const POLL_INTERVAL = 5000

	////////////////////////////////////////////////////////////
	// Polling logic
	////////////////////////////////////////////////////////////

	function startPolling() {
		if (isPolling) {
			console.log("Polling already active; skipping...")
			return
		}
		console.log("Starting polling...")
		isPolling = true
		pollStatus()
	}

	function stopPolling() {
		isPolling = false
		if (pollTimer) {
			clearTimeout(pollTimer)
			pollTimer = null
		}
	}

	async function pollStatus() {
		// If offline, schedule a retry, do nothing else
		if (!isOnline.value) {
			scheduleNextPoll()
			return
		}

		try {
			// Make a single fetch call
			const response = await fetch(
				`${getURL("add-item", "")}status?id=${itemData.id}`
			)
			const data = await response.json()
			if (data.error) {
				// Some error from server
				itemData.status = "error"
				errorMessage.value = data.error
				stopPolling()
				return
			}

			if (data.status === "complete") {
				// If there's an error in the output
				if (data.output?.error) {
					itemData.status = "error"
					errorMessage.value = data.output.error
				} else {
					itemData.status = "completed"
					// If you want to remove from queue:
					await iqStore.remove(itemData.id)
					if (!userStore.isViewingUser()) lastMealAdded.value.push(data.output)
				}
				stopPolling()
			} else if (data.status === "errored") {
				itemData.status = "error"
				errorMessage.value = "Something happened"
				stopPolling()
			} else {
				// Otherwise keep processing
				itemData.status = "processing"
				statusUpdate.value = "Processing..."
				scheduleNextPoll()
			}
		} catch (err) {
			// Catch network errors or other issues
			console.error("Polling error:", err)
			itemData.status = "error"
			errorMessage.value = err.message
			stopPolling()
		}
	}

	function scheduleNextPoll() {
		if (!isPolling) return
		if (pollTimer) {
			clearTimeout(pollTimer)
		}
		pollTimer = setTimeout(() => {
			pollStatus()
		}, POLL_INTERVAL)
	}

	////////////////////////////////////////////////////////////
	// Sending logic
	////////////////////////////////////////////////////////////

	async function trySendToServer() {
		if (isSending) {
			console.log("Already sending; skip")
			return
		}
		if (itemData.has_sent) {
			console.log("Already sent; skip")
			return
		}
		if (!isOnline.value) {
			console.log("Offline, cannot send")
			return
		}

		isSending = true

		try {
			// Prepare form data
			const formData = new FormData()
			const utcDate = moment(itemData.mealDateTime).utc()
			formData.append("timestamp", utcDate.unix())
			if (itemData.mealType) formData.append("mealType", itemData.mealType)
			if (itemData.cookType) formData.append("cookType", itemData.cookType)
			if (itemData.prompt) formData.append("prompt", itemData.prompt)
			if (itemData.restaurantName) {
				formData.append("restaurant_name", itemData.restaurantName)
			}
			if (itemData.category) formData.append("category", itemData.category)

			if (itemData.imageSrc) {
				// console.log(itemData.imageSrc)
				// const ext = itemData.imageSrc.split("/")[1].split(";")[0]
				const resp = await fetch(itemData.imageSrc)
				const blob = await resp.blob()
				// console.log(blob)
				const ext = blob.type.split("/")[1]
				formData.append("file", blob, `image.${ext}`)
				// const blob = await resp.blob()
				// formData.append("file", blob, "image.png")
				// formData.append("file", itemData.imageSrc, "image." + itemData.imageExt)
			}

			const response = await fetchWithAuth("/api/add-queue", {
				method: "POST",
				body: formData,
			})
			const data = await response.json()

			if (!response.ok) {
				// Error from server
				itemData.status = "error"
				errorMessage.value = data.message || data.error
				scheduleRetry()
			} else {
				// Success! Update local data
				const oldId = itemData.id
				itemData.id = data.id
				itemData.file_url = data.file_url
				// itemData.dp = data.dp
				itemData.timestamp = data.timestamp
				itemData.status = "processing"
				itemData.has_sent = true

				if (retryTimer) {
					clearTimeout(retryTimer)
					retryTimer = null
				}

				// Persist changes
				await iqStore.update(oldId, itemData)

				console.log("Sent to server successfully")
				startPolling()
			}
		} catch (err) {
			console.error("Could not send to server:", err)
			scheduleRetry()
		} finally {
			isSending = false
		}
	}

	function scheduleRetry() {
		if (!isOnline.value) return
		if (retryTimer) clearTimeout(retryTimer)
		retryTimer = setTimeout(() => {
			console.log("Retrying send to server...")
			itemData.status = "sending"
			errorMessage.value = null
			trySendToServer()
		}, 10000)
	}

	////////////////////////////////////////////////////////////
	// Retry / restart
	////////////////////////////////////////////////////////////
	function retryProcess() {
		// if (!itemData.has_sent) {
		// item never posted
		itemData.status = "sending"
		itemData.has_sent = false
		errorMessage.value = null
		trySendToServer()
		// } else {
		// 	// already on server, do a server-side restart
		// 	doRestartOnServer()
		// }
	}

	// async function doRestartOnServer() {
	// 	if (!isOnline.value) return
	// 	try {
	// 		const response = await fetch(
	// 			`${getURL("add-item", "")}restart?id=${itemData.id}`,
	// 			{
	// 				method: "POST",
	// 				headers: { "Content-Type": "application/json" },
	// 			}
	// 		)
	// 		const data = await response.json()
	// 		if (data.ok) {
	// 			itemData.status = "processing"
	// 			errorMessage.value = null
	// 			startPolling()
	// 		} else {
	// 			// If the server responded not ok, or no such item
	// 			iqStore.remove(itemData.id)
	// 		}
	// 	} catch (err) {
	// 		console.error(err)
	// 		itemData.status = "error"
	// 		errorMessage.value = err.message
	// 		stopPolling()
	// 	}
	// }

	////////////////////////////////////////////////////////////
	// Cancel / remove
	////////////////////////////////////////////////////////////

	// async function cancelProcess() {
	// 	const q = await Swal.fire({
	// 		icon: "question",
	// 		title: "Are you sure you want to cancel this item from the queue?",
	// 		showDenyButton: true,
	// 		confirmButtonText: "Yes",
	// 		denyButtonText: "No",
	// 		reverseButtons: true,
	// 	})

	// 	if (!q.isConfirmed) return

	// 	// If it has a server ID and status not sending, attempt to cancel on server
	// 	if (itemData.id && itemData.status !== "sending") {
	// 		if (isOnline.value) {
	// 			try {
	// 				const response = await fetch(
	// 					`${getURL("add-item", "")}terminate?id=${itemData.id}`,
	// 					{
	// 						method: "DELETE",
	// 						headers: { "Content-Type": "application/json" },
	// 					}
	// 				)
	// 				const data = await response.json()
	// 				if (data.ok) {
	// 					stopPolling()
	// 					iqStore.remove(itemData.id)
	// 				}
	// 			} catch (err) {
	// 				console.log(err)
	// 				stopPolling()
	// 				iqStore.remove(itemData.id)
	// 			}
	// 		} else {
	// 			// offline
	// 			stopPolling()
	// 			iqStore.remove(itemData.id)
	// 		}
	// 	} else {
	// 		// local item or 'sending'
	// 		iqStore.remove(itemData.id)
	// 	}
	// }

	async function removeProcess() {
		const q = await Swal.fire({
			icon: "question",
			title: "Are you sure you want to remove this item from the queue?",
			showDenyButton: true,
			confirmButtonText: "Yes",
			denyButtonText: "No",
			reverseButtons: true,
		})

		if (q.isConfirmed) {
			stopPolling()
			await iqStore.remove(itemData.id)
		}
	}

	////////////////////////////////////////////////////////////
	// Watch network changes
	////////////////////////////////////////////////////////////
	watch(isOnline, (online) => {
		if (!online) {
			stopPolling()
			return
		}
		// If we come back online, see if we should send or poll
		if (itemData.status === "sending") {
			if (!itemData.has_sent) {
				trySendToServer()
			} else {
				// has_sent but still 'sending'? We'll set to processing
				itemData.status = "processing"
				startPolling()
			}
		} else if (itemData.status === "processing" && itemData.id) {
			startPolling()
		}
	})

	////////////////////////////////////////////////////////////
	// Lifecycle
	////////////////////////////////////////////////////////////
	onMounted(() => {
		// If item is queued to send, do so if we're online
		if (itemData.status === "sending") {
			if (isOnline.value && !itemData.has_sent) {
				trySendToServer()
			} else if (isOnline.value && itemData.has_sent) {
				// Means we already have a server ID
				itemData.status = "processing"
			}
		} else if (
			itemData.status === "processing" &&
			itemData.id &&
			isOnline.value
		) {
			// If we have an ID, poll for completion
			startPolling()
		}
	})

	onBeforeUnmount(() => {
		// Cleanup timers
		stopPolling()
		if (retryTimer) {
			clearTimeout(retryTimer)
		}
		isSending = false
		isPolling = false
	})
</script>

<style lang="less" scoped>
	.card {
		border: none;
		border-radius: 8px;
		padding: 16px;
		box-shadow: none;
		max-width: 400px;
		margin: auto;
		text-align: center;
		width: 100%;

		> h2 {
			color: #ff6600;
		}
	}

	.image {
		::v-deep(> img) {
			width: 100%;
			height: auto;
			border-radius: 8px;
		}
	}

	.status {
		margin-top: 16px;
	}

	.status-text {
		font-size: 1.2em;
		font-weight: bold;
		color: #ff6600;
	}

	.loading-spinner {
		width: 100%;
		height: 50px;
		background: linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%);
		background-size: 200% 100%;
		animation: loading 1.5s infinite;
		position: relative;
		display: flex;
		align-items: center;
		justify-content: center;
	}

	.status-update {
		position: absolute;
		font-size: 1em;
		color: #333;
	}

	@keyframes loading {
		0% {
			background-position: 200% 0;
		}
		100% {
			background-position: -200% 0;
		}
	}

	.error {
		color: red;
		font-weight: bold;
	}

	.network-offline {
		color: red;
		font-weight: bold;
		margin-bottom: 8px;
	}
</style>
